import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { getCountries, type Country } from '../../api/publicApi';

/**
 * Country-code picker shared by the public phone-verification steps
 * (PublicEventDetail and PublicInvitation). Plain inline styles + Tailwind
 * only: this file must not import baseui / styletron-react (SAM-175).
 */

export const DEFAULT_COUNTRY_CODE = '+1';
export const DEFAULT_COUNTRY_FLAG = 'https://raw.githubusercontent.com/lipis/flag-icon-css/master/flags/4x3/do.svg';

const withPlus = (code: string) => (code.startsWith('+') ? code : `+${code}`);

/** Keep only what a national number may contain while the user types. */
export const sanitizeNationalNumber = (value: string) => value.replace(/[^\d\s\-()]/g, '');

/** `countryCode + digits` — the E.164 string the backend expects. */
export const buildFullPhone = (countryCode: string, nationalNumber: string) =>
  `${withPlus(countryCode)}${nationalNumber.replace(/\D/g, '')}`;

export interface SplitPhone {
  countryCode: string;
  flag?: string;
  nationalNumber: string;
}

/**
 * Split a stored phone into country code + national number using the
 * longest matching `phone_code` from the loaded country list. Numbers
 * without a leading '+' are treated as national numbers as-is.
 */
export const splitPhoneByCountry = (tel: string, countries: Country[] | undefined): SplitPhone | null => {
  const raw = tel.trim();
  if (!raw) return null;
  if (!raw.startsWith('+')) return { countryCode: '', nationalNumber: raw.replace(/\D/g, '') };
  const digits = raw.replace(/\D/g, '');
  let best: Country | undefined;
  for (const c of countries || []) {
    const code = (c.phone_code || '').replace(/\D/g, '');
    if (!code || !digits.startsWith(code)) continue;
    if (!best || code.length > best.phone_code.replace(/\D/g, '').length) best = c;
  }
  if (!best) {
    // Country list unavailable or no match: fall back to the default code
    // when it fits, otherwise keep the whole thing as the national part.
    const def = DEFAULT_COUNTRY_CODE.replace(/\D/g, '');
    if (digits.startsWith(def)) return { countryCode: DEFAULT_COUNTRY_CODE, nationalNumber: digits.slice(def.length) };
    return { countryCode: '', nationalNumber: digits };
  }
  const codeDigits = best.phone_code.replace(/\D/g, '');
  return { countryCode: withPlus(best.phone_code), flag: best.country_flag, nationalNumber: digits.slice(codeDigits.length) };
};

interface UsePhoneCountryOptions {
  apiKey?: string | null;
  enabled?: boolean;
}

export const usePhoneCountry = ({ apiKey, enabled = true }: UsePhoneCountryOptions) => {
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [countryFlag, setCountryFlag] = useState(DEFAULT_COUNTRY_FLAG);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');

  const { data: countries, isError: countriesError } = useQuery({
    queryKey: ['countries', apiKey],
    queryFn: () => getCountries(apiKey || undefined),
    enabled,
    staleTime: Infinity,
  });

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (countries || []).filter(c =>
      !q || (c.country || '').toLowerCase().includes(q) || (c.phone_code || '').includes(q)
    );
  }, [countries, search]);

  const selectCountry = (c: Country) => {
    setCountryCode(withPlus(c.phone_code));
    setCountryFlag(c.country_flag);
    setShowPicker(false);
    setSearch('');
  };

  /**
   * Apply a stored E.164 number: returns the national part to put in the
   * input and switches the selected country when the prefix is recognised.
   * When the prefix matches the currently selected code (e.g. several +1
   * countries) the current flag is kept.
   */
  const applyStoredPhone = (tel: string): string => {
    const split = splitPhoneByCountry(tel, countries);
    if (!split) return '';
    if (split.countryCode && split.countryCode !== countryCode) {
      setCountryCode(split.countryCode);
      if (split.flag) setCountryFlag(split.flag);
    }
    return split.nationalNumber;
  };

  return {
    countryCode, countryFlag, showPicker, setShowPicker, search, setSearch,
    countries, countriesReady: !!countries || countriesError, filteredCountries,
    selectCountry, applyStoredPhone,
    buildFullPhone: (nationalNumber: string) => buildFullPhone(countryCode, nationalNumber),
  };
};

const fieldStyle: React.CSSProperties = {
  padding: '12px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '16px',
  color: '#000', boxSizing: 'border-box', fontFamily: 'inherit',
};

interface CountryCodeButtonProps {
  countryCode: string;
  flag?: string;
  onClick: () => void;
}

export const CountryCodeButton: React.FC<CountryCodeButtonProps> = ({ countryCode, flag, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-haspopup="dialog"
    style={{
      ...fieldStyle, backgroundColor: '#F9FAFB', whiteSpace: 'nowrap', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0,
    }}
  >
    {flag && <img src={flag} alt="" style={{ width: '20px', height: '15px' }} />}
    <span>{countryCode}</span>
    <ChevronDown size={14} color="#6B7280" />
  </button>
);

interface CountryPickerModalProps {
  countries: Country[];
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (country: Country) => void;
  onClose: () => void;
  searchPlaceholder: string;
}

export const CountryPickerModal: React.FC<CountryPickerModalProps> = ({
  countries, search, onSearchChange, onSelect, onClose, searchPlaceholder,
}) => (
  <div
    role="dialog"
    aria-modal="true"
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '400px',
        maxHeight: '70vh', padding: '16px', display: 'flex', flexDirection: 'column',
      }}
    >
      <input
        autoFocus
        value={search}
        onChange={e => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        style={{ ...fieldStyle, width: '100%', outline: 'none', marginBottom: '8px' }}
      />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {countries.map(c => (
          <div
            key={c.country_code}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(c)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(c); } }}
            className="hover:bg-[#F3F3F3]"
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 8px',
              cursor: 'pointer', borderRadius: '6px', fontSize: '14px',
            }}
          >
            <img src={c.country_flag} alt="" style={{ width: '24px', height: '18px', borderRadius: '2px' }} />
            <span style={{ flex: 1 }}>{c.country}</span>
            <span style={{ color: '#6B7280' }}>{c.phone_code}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);
